import {
  list_product_summaries,
  get_product,
  put_product,
} from '@/api/product';
import {
  ProductTypeEnum,
  CurrencyEnum,
  ReportAggregationGroupEnum,
  PhysicalRealTicketTypeEnum,
} from '@/api/types';
import VueI18n, { IVueI18n } from 'vue-i18n';

export const productById = (products: list_product_summaries.Product[]) => {
  return new Map(products.map(p => [p.id, p]));
};

export const getProductToUpdateProduct = (getProduct: get_product.Product) => {
  const updateProduct: put_product.Product = {
    id: getProduct.id,
    type: getProduct.type,
    name: getProduct.name,
    nameEn: getProduct.nameEn,
    metas: getProduct.metas,
    timezone: getProduct.timezone,
    datePeriod: getProduct.datePeriod,
    fiscalYear: getProduct.fiscalYear,
    orderUnit: getProduct.orderUnit,
    price: getProduct.price,
    reportAggregationGroup: getProduct.reportAggregationGroup,
    physicalRealTicketType: getProduct.physicalRealTicketType,
    version: getProduct.version,
  };
  if (getProduct.categories) {
    updateProduct.categories = getProduct.categories;
  }
  if (getProduct.categoriesEn) {
    updateProduct.categoriesEn = getProduct.categoriesEn;
  }
  if (getProduct.activityId !== void 0) {
    updateProduct.activityId = getProduct.activityId;
  }
  if (getProduct.planId !== void 0) {
    updateProduct.planId = getProduct.planId;
  }
  if (getProduct.startTimeId !== void 0) {
    updateProduct.startTimeId = getProduct.startTimeId;
  }
  if (getProduct.unitId !== void 0) {
    updateProduct.unitId = getProduct.unitId;
  }
  if (getProduct.languageCode !== void 0) {
    updateProduct.languageCode = getProduct.languageCode;
  }
  if (getProduct.depositPlanId !== void 0) {
    updateProduct.depositPlanId = getProduct.depositPlanId;
  }
  if (getProduct.depositNetPrices !== void 0) {
    updateProduct.depositNetPrices = getProduct.depositNetPrices;
  }
  if (getProduct.withdrawalPlanId !== void 0) {
    updateProduct.withdrawalPlanId = getProduct.withdrawalPlanId;
  }
  if (getProduct.withdrawalNetPrices !== void 0) {
    updateProduct.withdrawalNetPrices = getProduct.withdrawalNetPrices;
  }
  return updateProduct;
};

export const newDefaultProductCategories = () => ['', '', ''];

export const newDefaultProduct = () => {
  const today = new Date();
  return {
    type: ProductTypeEnum.REAL_TICKET,
    name: '',
    nameEn: '',
    metas: [],
    timezone: '',
    datePeriod: {},
    fiscalYear: today.getFullYear(),
    orderUnit: {},
    price: {
      currency: CurrencyEnum.JPY,
      listPrice: 1,
      agencyCancelFee: 0,
      netPrices: [
        {
          commissionRatePercent: 0,
          netPrice: 0,
        },
      ],
    },
    depositNetPrices: [
      {
        commissionRatePercent: 0,
        netPrice: 0,
      },
    ],
    withdrawalNetPrices: [],
    reportAggregationGroup:
      ReportAggregationGroupEnum.UNKNOWN_REPORT_AGGREGATION_GROUP,
    physicalRealTicketType: PhysicalRealTicketTypeEnum.TICKET_NONE,
  } as put_product.Product;
};

interface Product {
  productId: string;
  ticketBlocks: TicketBlock[];
}

interface TicketBlock {
  ticketNumberStart: string;
  ticketNumberEnd: string;
  amount?: number;
}

export const productsWithTicketBlocksText = (
  products: Product[],
  productSummaries: Map<string, list_product_summaries.Product>,
  multiline = false
) => {
  let text = '';
  for (let i = 0; i < (products || []).length; i++) {
    const productSummary = productSummaries
      ? productSummaries.get(products[i].productId)
      : null;
    const prefix = productSummary ? `${productSummary.name}: ` : '';
    let ticketBlocksText = '[';
    for (let j = 0; j < products[i].ticketBlocks.length; j++) {
      ticketBlocksText += `${products[i].ticketBlocks[j].ticketNumberStart}~${products[i].ticketBlocks[j].ticketNumberEnd}`;
      if (j !== products[i].ticketBlocks.length - 1) {
        ticketBlocksText += ', ';
      }
    }
    ticketBlocksText += ']';
    text += `${prefix}${ticketBlocksText}`;
    if (i !== (products || []).length - 1) {
      text += multiline ? '<br>' : ', ';
    }
  }
  return text;
};

// FIXME: This may need to switch to call backend api
export const calculateTicketBlockAmount = (
  ticketNumberStart: string,
  ticketNumberEnd: string
) => {
  const start = parseInt(ticketNumberStart, 10);
  const end = parseInt(ticketNumberEnd, 10);
  if (isNaN(start) || isNaN(end)) {
    if (!ticketNumberStart || !ticketNumberEnd) {
      return 0;
    } else if (ticketNumberStart === ticketNumberEnd) {
      return 1;
    } else {
      return 2;
    }
  } else if (start > end) {
    return 0;
  } else {
    return end - start + 1;
  }
};

export const ticketBlocksWithoutProductNameText = (
  $i18n: VueI18n & IVueI18n,
  ticketBlocks: TicketBlock[],
  multiline = false
) => {
  let text = '';
  for (let i = 0; i < (ticketBlocks || []).length; i++) {
    const suffix =
      ticketBlocks[i].amount === void 0
        ? ''
        : `(${$i18n.t('Amount')}: ${ticketBlocks[i].amount})`;
    text += `${ticketBlocks[i].ticketNumberStart}~${ticketBlocks[i].ticketNumberEnd}${suffix}`;
    if (i !== (ticketBlocks || []).length - 1) {
      text += multiline ? '<br>' : ', ';
    }
  }
  return text;
};

export const ticketNumberInTicketBlock = (
  ticketNumber: string,
  ticketNumberStart: string,
  ticketNumberEnd: string
) => {
  const target = parseInt(ticketNumber, 10);
  const start = parseInt(ticketNumberStart, 10);
  const end = parseInt(ticketNumberEnd, 10);
  if (isNaN(start) || isNaN(end) || isNaN(target)) {
    return (
      ticketNumber === ticketNumberStart || ticketNumber === ticketNumberEnd
    );
  } else {
    return target >= start && target <= end;
  }
};
